import { useEffect, useRef, useState } from 'react'

import './_profile.scss'
// import { useTranslation } from 'react-i18next'
import {
  useQuery,
  useQueryClient
} from 'react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'
import styled from 'styled-components'

import { addToast } from 'components/Toast/Toast'
import VerifyNav from 'components/UI/VerifyNav/VerifyNav'
import { FreeTrialModal } from 'components-ref/organisms'
// import { AnnouncementsModal } from 'components-ref/organisms/modals'
import { PostsFeed } from 'components-ref/templates'
import { useHeaderLayout } from 'context/headerLayoutContext'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
// import DesktopStories from 'features/DesktopStories/DesktopStories'
import CustomPullToRefreshProfile from 'helpers/customPullToRefreshProfile'
// import { useProfileFanSessionTracking } from 'helpers/hooks'
import { trackProfileView } from 'helpers/profile/profileViewTracking'
import WithoutHeaderSection from 'layouts/WithoutHeaderSection/WithoutHeaderSection'
// import { getAnnouncements } from 'services/endpoints/announcement'
import { NotFound } from 'pages/404'
import { trackPageView } from 'services/analytics/googleAnalytics'
import { getPerformerByUsername } from 'services/endpoints/profile'
import { resolveTrialLink } from 'services/endpoints/settings/general/subscription'
import { useAuthStore, useUserProfile } from 'store'
import { useGlobalStore } from 'store/global'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { useProfileFeedCacheStore } from 'store/profileFeedCacheStore'
import screens from 'styles/screens'
import { type StoryPost } from 'types/api/story'
import { type IProfile } from 'types/interfaces/IProfile'

import { ProfileHolder } from './features/ProfileHolder'

export const Profile = () => {
const { setBackgroundColor, setHideFooter } = useHeaderLayout()
  const { setTitle } = usePageTitle()
  // const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { username, id } = useParams<{
    username?: string
    id?: string
  }>()
  const { accountSetupPopup } = useGlobalStore()
  const userData = useUserProfile()
  const tab = location?.state?.tabName
  const [stories, setStories] = useState<StoryPost[]>([])
  const scrollRef = useRef<HTMLDivElement>(null)
  const { getFeed, setFeed } = useProfileFeedCacheStore()
  const [feedPosts, setFeedPosts] = useState<any[]>([])
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false)
  const [freeTrialModalOpen, setFreeTrialModalOpen] = useState(true)
  // const [announcementsOpen, setAnnouncementsOpen] = useState(true)

  /*
   * A normal profile route provides :username.
   * A free-trial route provides :id, which is the trial_code.
   */
  const isTrialLink = !!id && !username

  /*
   * Resolve a trial link directly inside Profile.
   *
   * This only resolves/validates the link and tells us which creator
   * profile belongs to it. It does not accept or consume the trial.
   */
const {
  data: trialData,
  isLoading: isTrialLoading,
  isError: isTrialError
} = useQuery(
  ['trialLink', id],
  async () => await resolveTrialLink(id ?? ''),
  {
    enabled: isTrialLink && !!id,
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: resp => {
      if (
        resp?.user?.username &&
        resp?.user?.id
      ) {
        useProfileCacheStore
          .getState()
          .setProfile(
            resp.user.username,
            resp.user,
            userData?.id ?? null
          )
      }
    }
  }
)

  /*
   * For a normal profile this is simply :username.
   *
   * For /trial-link/:id, the creator username comes from the
   * successfully resolved trial.
   */
  const profileUsername =
    username ??
    trialData?.user?.username

  /*
   * Handle invalid trial links.
   *
   * Successful trial links remain on this Profile component.
   * There is no success redirect or intermediate trial page.
   */
  useEffect(() => {
    if (!isTrialLink || isTrialLoading || !trialData) return

    if (trialData?.error) {
      addToast('error', trialData.error)
      navigate('/', { replace: true })
      return
    }

    if (!trialData?.trial || !trialData?.user?.username) {
      addToast('error', 'Unable to resolve free trial.')
      navigate('/', { replace: true })
      return
    }

    if (trialData.trial.active === 0) {
      addToast('error', 'Trial has expired.')
      navigate('/', { replace: true })
      return
    }

    if (trialData.user.isSubscribed) {
      addToast(
        'error',
        'You are already subscribed to this user.'
      )
      navigate(`/${trialData.user.username}`, {
        replace: true
      })
      return
    }

    if (
      userData?.id &&
      trialData.user.id === userData.id
    ) {
      addToast(
        'error',
        "You can't start your own free trial."
      )
      navigate(`/${trialData.user.username}`, {
        replace: true
      })
    }
  }, [
    isTrialLink,
    isTrialLoading,
    trialData,
    userData?.id,
    navigate
  ])


  const trackCurrentProfileView = async (
  isSubscribed: boolean
): Promise<void> => {
  if (
    !userData?.id ||
    !profileData?.id ||
    isViewingOwnProfile
  ) {
    return
  }

  await trackProfileView({
    viewerId: userData.id,
    profileId: profileData.id,
    isSubscribed
  })
}
  // ===== viewer-scoped cache helpers =====
const setProfileInCache = useProfileCacheStore(state => state.setProfile)

const viewerId = userData?.id ?? null
const cachedProfile = useProfileCacheStore(state =>
  profileUsername
    ? state.getProfile(profileUsername, viewerId)
    : undefined
)
  // Pull the viewer-scoped cached profile


const isViewingOwnProfile =
  !!userData?.username &&
  !!profileUsername &&
  userData.username.toLowerCase() === profileUsername.toLowerCase()

  useEffect(() => {
    setTitle('Profile')
  }, [setTitle])

  useEffect(() => {
    setBackgroundColor('#fff')
    return () => setBackgroundColor(undefined)
  }, [setBackgroundColor])

  // Load cached feed posts
  useEffect(() => {
    const cached = getFeed(profileUsername ?? '')
    if (cached) setFeedPosts(cached)
  }, [profileUsername])

  // const shouldFetchAnnouncements = !!userData && !sessionStorage.getItem('hasFetchedAnnouncements')

  // -------- Profile query (cache-first + keep previous) --------
  const PROFILE_KEY = ['profile', profileUsername]

const {
  data,
  isLoading,
  isFetching,
  isError
} = useQuery<IProfile>(
  PROFILE_KEY,
  async () => {
    if (!profileUsername) return undefined
    return await getPerformerByUsername(profileUsername)
  },
  {
enabled:
  !!profileUsername &&
  !isViewingOwnProfile &&
  !isTrialLink,
    initialData: cachedProfile,
    keepPreviousData: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    retry: false,
    onSuccess: (fresh) => {
      if (fresh && profileUsername) {
        setProfileInCache(
          profileUsername,
          fresh,
          viewerId
        )
      }
    },
  }
)
useEffect(() => {
  if (
    !userData?.id ||
    !userData?.username ||
    !profileUsername
  ) return

  const sameUser =
    userData.username.toLowerCase() === profileUsername.toLowerCase()

  if (!sameUser) return

  const vId = userData.id

  const existing = useProfileCacheStore
    .getState()
    .getProfile(userData.username, vId)

  if (!existing) {
    setProfileInCache(userData.username, userData as unknown as IProfile, vId)

    if (userData.username !== profileUsername) {
      setProfileInCache(profileUsername, userData as unknown as IProfile, vId)
    }
  }
}, [
  userData?.id,
  userData?.username,
  profileUsername,
  setProfileInCache,
])



  // Refetch once when logging in (unauth → auth) while viewing someone else's profile
  const prevUserIdRef = useRef<string | null>(userData?.id ?? null)
  useEffect(() => {
    const prev = prevUserIdRef.current
    const curr = userData?.id ?? null
    if (!isViewingOwnProfile && curr && prev !== curr && profileUsername) {
      queryClient.invalidateQueries(PROFILE_KEY)
    }
    prevUserIdRef.current = curr
  }, [
    userData?.id,
    isViewingOwnProfile,
    profileUsername,
    queryClient
  ])

  // Creator viewing own profile – no query needed
  const profileData: IProfile | undefined = isViewingOwnProfile
    ? (cachedProfile ?? (userData as unknown as IProfile))
    : (data ?? cachedProfile)


  // Track fan sessions
/*   const shouldTrackFanSession =
    !!userData?.id &&
    !!profileData?.id &&
    !isViewingOwnProfile &&
    profileData.isSubscribed

useProfileFanSessionTracking(
    profileData?.id ?? 0,
    shouldTrackFanSession
) */


// Google Analytics — verified creator profile visits
useEffect(() => {
  if (isTrialLink) return
  if (!profileUsername || !profileData?.id) return
  if (profileData.role !== 'model') return

  // Important because the profile query uses
  // keepPreviousData: true.
  if (
    profileData.username?.toLowerCase() !==
    profileUsername.toLowerCase()
  ) {
    return
  }

  const { accessToken } = useAuthStore.getState()

  trackPageView({
    path: `/${encodeURIComponent(profileData.username)}`,
    pageType: 'creator_profile',
    title: 'Creator Profile | AllFans',
    visitorStatus: accessToken
      ? 'authenticated'
      : 'guest',
    navigationKey: location.key
  })
}, [
  isTrialLink,
  profileUsername,
  profileData?.id,
  profileData?.username,
  profileData?.role,
  location.key
])

useEffect(() => {
  if (
    !profileData?.id ||
    !userData?.id ||
    isViewingOwnProfile
  ) {
    return
  }

  const isSubscribed = profileData.isSubscribed

  /*
   * Locked profile: there is no useful feed request to wait for.
   */
  if (!isSubscribed) {
    void trackCurrentProfileView(false)
  }
}, [
  profileData?.id,
  profileData?.isSubscribed,
  userData?.id,
  isViewingOwnProfile
])


  
  // HIDE FOOTER WHEN SCROLLING DOWN FEED AND IT WILL REAPPEAR WHEN SCROLLING BACK UP

useEffect(() => {
  let lastTop = window.scrollY
  let ticking = false
  let userStartedScrolling = false

  const SHOW_AT_TOP_PX = 20
  const HIDE_AFTER_SCROLL_PX = 80
  const DELTA_PX = 8
  const BOTTOM_THRESHOLD_PX = 10

  const setChromeHidden = (hidden: boolean) => {
    document.body.classList.toggle('footer-hidden', hidden)
    document.body.classList.toggle('header-hidden', hidden)
  }

  const markUserScrolling = () => {
    userStartedScrolling = true
    lastTop = window.scrollY
  }

  document.body.classList.add('chrome-transition-disabled')
  setChromeHidden(false)

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.body.classList.remove('chrome-transition-disabled')
      lastTop = window.scrollY
    })
  })

  const onScroll = () => {
    if (!userStartedScrolling) {
      lastTop = window.scrollY
      return
    }

    const top = window.scrollY
    const delta = top - lastTop

    if (ticking) return

    ticking = true

    requestAnimationFrame(() => {
      ticking = false

      const atBottom =
        window.innerHeight + top >=
        document.documentElement.scrollHeight - BOTTOM_THRESHOLD_PX

      if (atBottom) {
        document.body.classList.remove('footer-hidden')
        lastTop = top
        return
      }

      if (top <= SHOW_AT_TOP_PX || top < HIDE_AFTER_SCROLL_PX) {
        setChromeHidden(false)
        lastTop = top
        return
      }

      if (Math.abs(delta) < DELTA_PX) {
        lastTop = top
        return
      }

      setChromeHidden(delta > 0)
      lastTop = top
    })
  }

  window.addEventListener('wheel', markUserScrolling, { passive: true })
  window.addEventListener('touchstart', markUserScrolling, { passive: true })
  window.addEventListener('scroll', onScroll, { passive: true })

  return () => {
    window.removeEventListener('wheel', markUserScrolling)
    window.removeEventListener('touchstart', markUserScrolling)
    window.removeEventListener('scroll', onScroll)

    document.body.classList.remove(
      'footer-hidden',
      'header-hidden',
      'chrome-transition-disabled'
    )
  }
}, [])

  // ------------------------------
  // PROFILE PULL-TO-REFRESH (clean)
  // - Trim ALL getFeed queries for this user down to page 0
  // - Refetch ONLY page 0
  // - Also refresh stories
  // ------------------------------
  const pullToRefreshProfile = async () => {
    const userId = profileData?.id
    if (!userId) return

    const feedQueries = queryClient.getQueryCache().findAll(['getFeed', userId])

    // 1) Trim cache to first page only (prevents refetching page 2+)
    for (const q of feedQueries) {
      queryClient.setQueryData(q.queryKey, (old: any) => {
        if (!old?.pages?.length) return old
        return {
          ...old,
          pages: [old.pages[0]],
          pageParams: [old.pageParams?.[0]],
        }
      })
    }

    // 2) Refetch ONLY first page for each active feed query
    await Promise.all(
      feedQueries.map(async q =>
        await queryClient.refetchQueries(q.queryKey, {
          active: true,
          refetchPage: (_page: any, index: number) => index === 0,
        } as any)
      )
    )

    // 3) Refresh stories
    await queryClient.invalidateQueries(['allStoryPosts'])
  }

  // Always run the same refresh actions
  const invalidateQueries = [pullToRefreshProfile]

  const refetchProfile = async () => {
    await queryClient.invalidateQueries(PROFILE_KEY)
  }

 /*  const { data: announcements } = useQuery(
    'getAnnouncements',
    getAnnouncements,
    {
      enabled: shouldFetchAnnouncements,
      refetchOnWindowFocus: false,
      onSuccess: () => {
        sessionStorage.setItem('hasFetchedAnnouncements', 'true')
      }
    }
  ) */
    

  const renderPostsFeed = () => {
    if (!profileData) return null

    const feedProps = {
      id: profileData.id,
      user: profileData,
      username: profileData.username,
      setSubscribeModalOpen,
      cachedPosts: getFeed(profileData.username),
      onPostsLoaded: (posts: any[]) => {
        const cached = getFeed(profileData.username)
        if (!cached || JSON.stringify(cached) !== JSON.stringify(posts)) {
          setFeed(profileData.username, posts)
        }
      }
    }

    return (
      <PostsFeed
        feedNav={['all', 'videos', 'premium', 'reels']}
        {...feedProps}
        onInitialFeedSettled={() => {
          void trackCurrentProfileView(true)
        }}
      />
    )
  }

  // --------- RENDER GUARDS ----------

  /*
   * A trial-link route cannot load the profile until its trial code
   * has resolved to a creator username.
   */
  if (
    isTrialLink &&
    isTrialLoading
  ) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 99999,
        }}
      >
        <BeatLoader size={12} color="#999" />
      </div>
    )
  }

  if (
    isTrialLink &&
    (
      isTrialError ||
      trialData?.error ||
      !trialData?.trial ||
      !trialData?.user?.username
    )
  ) {
    return <NotFound />
  }

  // Only blank if we truly have nothing to show
if (!isViewingOwnProfile && (isLoading || isFetching) && !cachedProfile && !data) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 99999,
      }}
    >
      <BeatLoader size={12} color="#999" />
    </div>
  )
}

  // Only 404 when not fetching and the final data says "not a model"
if (!isViewingOwnProfile && isError) {
  return <NotFound />;
}

if (
  !isViewingOwnProfile &&
  !isLoading &&
  !isFetching &&
  (!profileData || profileData.role !== 'model')
) {
  return <NotFound />;
}

  return (
    <WithoutHeaderSection customClass={`profile-woh ${profileData ? 'has-real-cover' : ''}`}>
      {freeTrialModalOpen &&
        isTrialLink &&
        trialData?.trial &&
        trialData?.user &&
        id && (
          <FreeTrialModal
            trial={trialData.trial}
            user={trialData.user}
            trialCode={id}
            onClose={() => setFreeTrialModalOpen(false)}
          />
      )}
{/*         {announcementsOpen && announcements?.length > 0 && userData?.role === 'model' && (
          <AnnouncementsModal announcements={announcements} onClose={() => setAnnouncementsOpen(false)} />
        )} */}
        {profileData && (
          <>
            <CustomPullToRefreshProfile scrollContainerRef={scrollRef} invalidateQueries={invalidateQueries}>
              <ProfileHolder
                scrollContainerRef={scrollRef}
                stories={stories}
                onStoryDelete={(storyId: string) => {
                  setStories(prev => prev.filter(story => story.id !== storyId))
                }}
                id={profileData?.id}
                role={profileData?.role}
                active_stream={profileData?.active_stream}
                stream_url={profileData?.stream_url}
                online={profileData?.online_status}
                last_logged_at={profileData?.last_logged_at}
                cover={profileData?.cover?.url ?? profileData?.cropped_cover?.url ?? null}
                avatar={profileData?.avatar?.url ?? null}
                display_name={profileData?.display_name}
                expiry_at={profileData?.subscription_detail?.[0]?.expiry_at}
                follower_count={profileData?.follower_count}
                friend_count={profileData?.friend_count}
                username={profileData?.username}
                show_stories={profileData?.show_stories}
                friend_button={profileData?.friend_button}
                has_stories={profileData?.has_stories}
                isSubscribed={profileData?.isSubscribed}
                subscription_plans={profileData?.subscription_plans}
                promo_campaign={profileData?.promo_campaign}
                refetchProfileData={refetchProfile}
                photo_count={profileData?.photo_count}
                premium_video_count={profileData?.premium_video_count}
                video_count={profileData?.video_count}
                friends_status={profileData?.friends_status}
                friendships={profileData?.friendship_status || {}}
                mini_bio={profileData?.mini_bio}
                subscribeModalOpen={subscribeModalOpen}
                setSubscribeModalOpen={setSubscribeModalOpen}
                show_follower_count={profileData?.follow_count}
                public_friends={profileData?.public_friends}
                show_likes_count={profileData?.like_count}
                show_mini_bio={profileData?.show_mini_bio}
                post_like_count={profileData?.post_like_count}
                show_online_status={profileData?.show_online_status}
                status={profileData?.status}
                overall_revenue={profileData?.overall_revenue}
                tab={location?.state?.tabName}
                rebill={profileData?.subscription_detail?.[0]?.rebill}
                updated_at={profileData?.subscription_detail?.[0]?.updated_at}
                ownProfile={isViewingOwnProfile}
              />
              {renderPostsFeed()}
            </CustomPullToRefreshProfile>
          </>
        )}
        <DesktopAdditionalContent>
          {/* <DesktopStories stories={stories} page='profile' /> */}
        </DesktopAdditionalContent>

        {accountSetupPopup.visible && (
          <VerifyNavWrapper>
            <VerifyNav isDesktop={true} />
          </VerifyNavWrapper>
        )}
    </WithoutHeaderSection>
  )
}

const VerifyNavWrapper = styled.div`
  @media (max-width: ${screens.medium}) {
    display: none;
  }
`